import AdvancedController from "./advanced-controller"
import { isDescendant } from "../utils/utils";
import { fadeIn, fadeOut } from '../animations/animation-fade'
import { Breakpoints } from "../utils/breakpoints";

const NAV_TOGGLE_BREAKPOINT = 'xl'
const NAV_MAIN_MODE_CLICK = 0;
const NAV_MAIN_MODE_HOVER = 1;

export default class NavMainSubMenuController extends AdvancedController {

    static targets = [
        // the "real" anchor link of the menu
        'anchor',
        // the submenu ul
        'subMenu'
    ];

    static values = {
        // is this submenu visible
        isVisible: Boolean,
        // the depth of this submenu
        depth: Number,
    }

    connect() {
        super.connect();

        // init a breakpoints manager for later queries
        this.breakpoints = new Breakpoints();

        // set this to false by default, we assume that on page load no submenu is visible
        this.isVisibleValue = false

        // outside click close, only for NAV_MAIN_MODE_CLICK
        if (Number(navMainSettings.mode) === NAV_MAIN_MODE_CLICK) {
            window.addEventListener('click', (e) => {
                if (this.isVisibleValue && !this.element.contains(e.target) && e.target.tagName !== 'A') {
                    this.isVisibleValue = false;
                    this._handleVisibility();
                }
            })
        }
    }

    initialize() {
        // determine if this is a page link or not (e.g. custom link, e.g. to #)
        // important for the click mode, when e.g. the parent link is a -page-link, you access the page by clicking
        // on the menu item, but when it is -no-page-link, it opens the submenu
        this.element.classList.add(this._isPageLink() ? '-page-link' : '-no-page-link');
    }

    // Click-Mode (0 = NAV_MAIN_MODE_CLICK) -> More Click
    onMoreClick(e) {
        e.preventDefault();
        if (Number(navMainSettings.mode) === NAV_MAIN_MODE_CLICK || this.breakpoints.breakpointDown(NAV_TOGGLE_BREAKPOINT)) {
            this._toggle();
            this._handleVisibility();
        }
    }

    /**
     * Called when a menu item is clicked. For page links nothing happens (2), i.e. the default browser behaviour is
     * triggered, which means the new page is loaded. For page links there are again two possibilities, either it is an 
     * on-page-anchor (1a; WP custom link with e.g. #section-1234), then we close the nav as the AnchorScroll scrolls
     * to the item, or it is a simple #-Anchor (1b) that will just toggle the sub nav in click NAV_MAIN_MODE_CLICK.
     * @param {*} e - Browser Event
     */
    onMenuItemClick(e) {
        if (!this._isPageLink()) { // (1)
            if (this._isOnPageLink(e)) { // (1a -> #section-123)
                // on page link with on-page-anchor is clicked, e.g. #section-123
                this.getController('header').messageCloseNav();
            }
            else if (e.target.href.endsWith('#')) {
                e.preventDefault();
                if (Number(navMainSettings.mode) === NAV_MAIN_MODE_CLICK) {
                    this._toggle();
                    this._handleVisibility();
                }
            }
            else { // (1b -> #)
                // custom link with only # in URL will toggle
                this.isVisibleValue = false;
                this.getController('header').messageCloseNav();
            }
        } else { // (2)
            // normal links to other pages -> do nothing
        }
    }

    // Hover-Mode (1 = NAV_MAIN_MODE_HOVER) -> Mouse Enter
    onMouseEnter(e) {
        if (Number(navMainSettings.mode) === NAV_MAIN_MODE_HOVER && this.breakpoints.breakpointUp(NAV_TOGGLE_BREAKPOINT)) {
            e.preventDefault();

            this.isVisibleValue = true;
            this._handleVisibility(e);
        }
    }

    // Hover-Mode (1 = NAV_MAIN_MODE_HOVER) -> Mouse Leave
    onMouseLeave(e) {
        // only handle hover mode
        if (Number(navMainSettings.mode) === NAV_MAIN_MODE_HOVER && this.breakpoints.breakpointUp(NAV_TOGGLE_BREAKPOINT)) {
            const toElement = e.relatedTarget || e.toElement
            // fixes issue with alt+tab (window looses focus while hover)
            if (!toElement) return;
            // check if the element we move the mouse to is a descendant, i.e. we move to another menu item or outside
            // of the menu, then we need to hide
            if (!isDescendant(this.element, toElement)) {
                e.preventDefault();
                this.isVisibleValue = false;
                this._handleVisibility(e);
            }
        }
    }

    _toggle() {
        this.isVisibleValue = !this.isVisibleValue
    }

    _handleVisibility(e) {
        if (this.isVisibleValue) {
            this._show();
            if (e && Number(navMainSettings.mode) === NAV_MAIN_MODE_HOVER) {
                const showMore = e.target.querySelector(':not(.sub-menu--depth-0) .nav-main__more')
                if (showMore)
                    showMore.classList.add("--showing-more");
            }
        } else {
            this._hide();
            if (e && Number(navMainSettings.mode) === NAV_MAIN_MODE_HOVER) {
                const showMore = e.target.querySelector(':not(.sub-menu--depth-0) .--showing-more')
                if (showMore)
                    showMore.classList.remove("--showing-more");
            }
        }
    }

    _show() {
        fadeIn(this.subMenuTarget);
        const closeIcon = document.querySelector('.header__background__icon--close');
        if (closeIcon)
            closeIcon.classList.add('is-active');
        this._closeSiblings();
    }

    _hide() {
        fadeOut(this.subMenuTarget);
        const closeIcon = document.querySelector('.header__background__icon--close');
        if (closeIcon) 
            closeIcon.classList.remove('is-active');
        this._closeSubmenus();
    }

    _isPageLink() {
        return !this.element.classList.contains('menu-item-type-custom') || this.anchorTarget.href === "#"
    }

    _isOnPageLink(e) {
        const linkHref = e.target.getAttribute('href');
        return e.target.tagName === 'A' && linkHref.indexOf("#") === 0 && linkHref.length > 1
    }

    _closeSubmenus() {
        const otherControllers = this.getSubControllers('nav-main-sub-menu')
        for (const otherController of otherControllers) {
            otherController.messageClose();
        }
    }

    _closeSiblings() {
        const siblings = this.getSiblingControllers('nav-main-sub-menu')
        for (const sibling of siblings) {
            sibling.messageClose();
        }
    }

    messageClose() {
        if (this.isVisibleValue) {
            this.isVisibleValue = false;
            this._handleVisibility();
        }
    }

}
